import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { applicantsAPI, formatApiErrorDetail } from '../../lib/api';
import { Plus, MagnifyingGlass, Eye, PencilSimple, UserCheck, Clock, CheckCircle, XCircle, Trash } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const REVIEW_STAGES = ['initial', 'documentation', 'interview', 'chapter_review', 'final'];
const STATUSES = ['pending_review', 'under_review', 'approved', 'rejected', 'withdrawn'];

const ApplicantsPage = () => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingApplicant, setEditingApplicant] = useState(null);
  const [viewingApplicant, setViewingApplicant] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', address: '',
    occupation: '', motivation: '', sponsor_member_id: '',
    status: 'pending_review', review_stage: 'initial', internal_notes: ''
  });

  const fetchData = async () => {
    try {
      const response = await applicantsAPI.getAll();
      setApplicants(response.data);
    } catch (error) {
      console.error('Error fetching applicants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredApplicants = applicants.filter(a => {
    const matchesSearch = a.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         a.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingApplicant) {
        await applicantsAPI.update(editingApplicant.id, formData);
        toast.success('Applicant updated successfully');
      } else {
        await applicantsAPI.create(formData);
        toast.success('Applicant created successfully');
      }
      setShowModal(false);
      setEditingApplicant(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleConvert = async (applicant) => {
    if (!window.confirm(`Convert ${applicant.full_name} to a member?`)) return;
    try {
      await applicantsAPI.convert(applicant.id);
      toast.success('Applicant converted to member successfully');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDelete = async (applicant) => {
    if (!window.confirm(`Are you sure you want to delete applicant "${applicant.full_name}"? This action cannot be undone.`)) return;
    try {
      await applicantsAPI.delete(applicant.id);
      toast.success('Applicant deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (applicant) => {
    setEditingApplicant(applicant);
    setFormData({
      full_name: applicant.full_name || '',
      email: applicant.email || '',
      phone: applicant.phone || '',
      address: applicant.address || '',
      occupation: applicant.occupation || '',
      motivation: applicant.motivation || '',
      sponsor_member_id: applicant.sponsor_member_id || '',
      status: applicant.status || 'pending_review',
      review_stage: applicant.review_stage || 'initial',
      internal_notes: applicant.internal_notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      full_name: '', email: '', phone: '', address: '',
      occupation: '', motivation: '', sponsor_member_id: '',
      status: 'pending_review', review_stage: 'initial', internal_notes: ''
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle size={16} className="text-green-600" />;
      case 'rejected': return <XCircle size={16} className="text-red-600" />;
      case 'under_review': return <Clock size={16} className="text-yellow-600" />;
      default: return <Clock size={16} className="text-[#8A847A]" />;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="applicants-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Applicants</h1>
            <p className="text-[#8A847A]">{applicants.length} total applicants</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setEditingApplicant(null); setShowModal(true); }}
            className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none"
            data-testid="add-applicant-button"
          >
            <Plus size={18} className="mr-2" />
            Add Applicant
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 bg-white border border-[#D8D2C4] p-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A847A]" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none border-[#D8D2C4]"
              data-testid="applicant-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 rounded-none" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#D8D2C4] overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <table className="w-full data-table" data-testid="applicants-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Review Stage</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplicants.map(applicant => (
                  <tr key={applicant.id} data-testid={`applicant-row-${applicant.id}`}>
                    <td className="px-4 py-3 font-medium text-[#1A1614]">{applicant.full_name}</td>
                    <td className="px-4 py-3 text-[#4A453E]">{applicant.email}</td>
                    <td className="px-4 py-3 text-[#4A453E] capitalize">{applicant.review_stage?.replace('_', ' ')}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(applicant.status)}
                        <span className="capitalize text-sm">{applicant.status?.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#8A847A] text-sm">
                      {applicant.created_at ? new Date(applicant.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => setViewingApplicant(applicant)}
                          className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                          data-testid={`view-applicant-${applicant.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button 
                          onClick={() => handleEdit(applicant)}
                          className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                          data-testid={`edit-applicant-${applicant.id}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                        {applicant.status !== 'approved' && applicant.status !== 'rejected' && (
                          <button 
                            onClick={() => handleConvert(applicant)}
                            className="p-2 hover:bg-green-50 text-green-600"
                            title="Convert to Member"
                            data-testid={`convert-applicant-${applicant.id}`}
                          >
                            <UserCheck size={18} />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDelete(applicant)}
                          className="p-2 hover:bg-red-50 text-red-600"
                          title="Delete Applicant"
                          data-testid={`delete-applicant-${applicant.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredApplicants.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[#8A847A]">
                      No applicants found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingApplicant ? 'Edit Applicant' : 'Add New Applicant'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="applicant-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                    className="rounded-none"
                    data-testid="applicant-input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="rounded-none"
                    data-testid="applicant-input-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Occupation</label>
                  <Input
                    value={formData.occupation}
                    onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="rounded-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Review Stage</label>
                  <Select value={formData.review_stage} onValueChange={(v) => setFormData({...formData, review_stage: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REVIEW_STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Motivation</label>
                <Textarea
                  value={formData.motivation}
                  onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                  rows={3}
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Internal Notes</label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => setFormData({...formData, internal_notes: e.target.value})}
                  rows={2}
                  className="rounded-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="applicant-submit-button">
                  {editingApplicant ? 'Update Applicant' : 'Create Applicant'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Modal */}
        <Dialog open={!!viewingApplicant} onOpenChange={() => setViewingApplicant(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Applicant Details</DialogTitle>
            </DialogHeader>
            {viewingApplicant && (
              <div className="space-y-6" data-testid="applicant-details">
                {/* Header */}
                <div className="pb-4 border-b border-[#D8D2C4]">
                  <h3 className="font-heading text-xl text-[#1A1614]">{viewingApplicant.full_name}</h3>
                  <p className="text-[#4A453E]">{viewingApplicant.email}</p>
                  <div className="flex gap-4 mt-2">
                    <span className={`px-2 py-1 text-xs ${
                      viewingApplicant.status === 'approved' ? 'bg-green-100 text-green-800' :
                      viewingApplicant.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {viewingApplicant.status?.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="text-xs text-[#8A847A]">
                      Stage: {viewingApplicant.review_stage?.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                {/* Personal Information */}
                <div>
                  <h4 className="font-heading text-lg text-[#7B1818] mb-3">Personal Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm bg-[#F5F2EB] p-4">
                    <div><span className="text-[#8A847A]">Full Name:</span> <span className="font-medium">{viewingApplicant.full_name || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Date of Birth:</span> <span className="font-medium">{viewingApplicant.application_data?.date_of_birth || viewingApplicant.date_of_birth || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Email:</span> <span className="font-medium">{viewingApplicant.email || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Phone:</span> <span className="font-medium">{viewingApplicant.phone || viewingApplicant.application_data?.phone || '-'}</span></div>
                    <div className="col-span-2"><span className="text-[#8A847A]">Address:</span> <span className="font-medium">{viewingApplicant.address || viewingApplicant.application_data?.address || '-'}</span></div>
                    <div><span className="text-[#8A847A]">City:</span> <span className="font-medium">{viewingApplicant.application_data?.city || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Country:</span> <span className="font-medium">{viewingApplicant.application_data?.country || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Nationality:</span> <span className="font-medium">{viewingApplicant.application_data?.nationality || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Occupation:</span> <span className="font-medium">{viewingApplicant.occupation || viewingApplicant.application_data?.occupation || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Employer:</span> <span className="font-medium">{viewingApplicant.application_data?.employer || '-'}</span></div>
                  </div>
                </div>

                {/* Faith Information */}
                <div>
                  <h4 className="font-heading text-lg text-[#7B1818] mb-3">Faith Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm bg-[#F5F2EB] p-4">
                    <div><span className="text-[#8A847A]">Christian Denomination:</span> <span className="font-medium">{viewingApplicant.application_data?.christian_denomination || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Church Name:</span> <span className="font-medium">{viewingApplicant.application_data?.church_name || '-'}</span></div>
                    <div><span className="text-[#8A847A]">Baptized:</span> <span className="font-medium">{viewingApplicant.application_data?.baptized === true ? 'Yes' : viewingApplicant.application_data?.baptized === false ? 'No' : '-'}</span></div>
                    <div><span className="text-[#8A847A]">Confirmed:</span> <span className="font-medium">{viewingApplicant.application_data?.confirmed === true ? 'Yes' : viewingApplicant.application_data?.confirmed === false ? 'No' : '-'}</span></div>
                  </div>
                </div>

                {/* Motivation & Background */}
                <div>
                  <h4 className="font-heading text-lg text-[#7B1818] mb-3">Motivation & Background</h4>
                  <div className="space-y-4 text-sm">
                    {(viewingApplicant.motivation || viewingApplicant.application_data?.motivation) && (
                      <div className="bg-[#F5F2EB] p-4">
                        <p className="text-[#8A847A] mb-1">Why do you wish to join the Order?</p>
                        <p className="text-[#4A453E]">{viewingApplicant.motivation || viewingApplicant.application_data?.motivation}</p>
                      </div>
                    )}
                    {viewingApplicant.application_data?.community_service && (
                      <div className="bg-[#F5F2EB] p-4">
                        <p className="text-[#8A847A] mb-1">Community Service Experience</p>
                        <p className="text-[#4A453E]">{viewingApplicant.application_data.community_service}</p>
                      </div>
                    )}
                    {viewingApplicant.application_data?.professional_background && (
                      <div className="bg-[#F5F2EB] p-4">
                        <p className="text-[#8A847A] mb-1">Professional Background</p>
                        <p className="text-[#4A453E]">{viewingApplicant.application_data.professional_background}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* References */}
                <div>
                  <h4 className="font-heading text-lg text-[#7B1818] mb-3">References</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {viewingApplicant.application_data?.reference_name_1 && (
                      <div className="bg-[#F5F2EB] p-4 text-sm">
                        <p className="font-medium text-[#1A1614] mb-2">Reference 1</p>
                        <div><span className="text-[#8A847A]">Name:</span> {viewingApplicant.application_data.reference_name_1}</div>
                        <div><span className="text-[#8A847A]">Email:</span> {viewingApplicant.application_data.reference_email_1 || '-'}</div>
                        <div><span className="text-[#8A847A]">Phone:</span> {viewingApplicant.application_data.reference_phone_1 || '-'}</div>
                        <div><span className="text-[#8A847A]">Relationship:</span> {viewingApplicant.application_data.reference_relationship_1 || '-'}</div>
                      </div>
                    )}
                    {viewingApplicant.application_data?.reference_name_2 && (
                      <div className="bg-[#F5F2EB] p-4 text-sm">
                        <p className="font-medium text-[#1A1614] mb-2">Reference 2</p>
                        <div><span className="text-[#8A847A]">Name:</span> {viewingApplicant.application_data.reference_name_2}</div>
                        <div><span className="text-[#8A847A]">Email:</span> {viewingApplicant.application_data.reference_email_2 || '-'}</div>
                        <div><span className="text-[#8A847A]">Phone:</span> {viewingApplicant.application_data.reference_phone_2 || '-'}</div>
                        <div><span className="text-[#8A847A]">Relationship:</span> {viewingApplicant.application_data.reference_relationship_2 || '-'}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Internal Notes */}
                {viewingApplicant.internal_notes && (
                  <div>
                    <h4 className="font-heading text-lg text-[#7B1818] mb-3">Internal Notes</h4>
                    <div className="bg-yellow-50 border border-yellow-200 p-4">
                      <p className="text-[#4A453E] text-sm">{viewingApplicant.internal_notes}</p>
                    </div>
                  </div>
                )}

                {/* Application Date */}
                <div className="text-xs text-[#8A847A] pt-4 border-t border-[#D8D2C4]">
                  Applied: {viewingApplicant.created_at ? new Date(viewingApplicant.created_at).toLocaleString() : '-'}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ApplicantsPage;
